import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Database, FileDown } from "lucide-react";
import { useFetch } from "@/lib/api";
import { Panel, StatusChip, KpiTile, EmptyState } from "@/components/app/primitives";
import { LiveBadge } from "@/components/app/livePrimitives";

// Live-engagement status block for the Executive Command Center.
// Reads only Triad-validated findings and computed readiness - never demo data.
export const LiveEngagementPanel = () => {
  const { data: s } = useFetch("/live/summary");
  const navigate = useNavigate();

  if (!s) return null;

  if (!s.active) {
    return (
      <Panel title="Live engagement status" testId="cc-live-panel" right={<LiveBadge />} className="mb-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-[--tx-2]">{s.message}</p>
          <button onClick={() => navigate("/live/intake")} data-testid="cc-live-goto-intake"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[--hairline] bg-[--shell-3] px-3 py-2 text-xs font-semibold text-[--tx-2] hover:text-[--tx-1] transition-colors shrink-0">
            <Database size={12} /> Open Data Intake Hub
          </button>
        </div>
      </Panel>
    );
  }

  const mr = s.metric_reconciliation || {};
  return (
    <Panel title="Live engagement status" testId="cc-live-panel"
      right={
        <div className="flex items-center gap-2">
          <LiveBadge />
          <span className="text-[10px] font-mono text-[--tx-3]">as-of {s.as_of} · {s.files_count} files</span>
        </div>
      }
      className="mb-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <KpiTile label="Data readiness" value={`${s.data_readiness?.score ?? "—"}`} sub={`${(s.data_readiness?.blockers || []).length} blocking gaps`} tone={s.data_readiness?.score >= 70 ? "validated" : "risk"} testId="cc-live-readiness" />
        <KpiTile label="Metric reconciliation" value={mr.months_overlapping ? `${mr.months_consistent}/${mr.months_overlapping}` : "—"} sub={`definition ${mr.definition_status}`} tone={mr.source_status === "consistent" ? "validated" : "risk"} testId="cc-live-reconciliation" />
        <KpiTile label="August forensic" value={s.forensic_status === "conclusive" ? "Conclusive" : "Provisional"} sub="per integrity gate" tone={s.forensic_status === "conclusive" ? "validated" : "risk"} testId="cc-live-forensic" />
        <KpiTile label="Confirmed drivers" value={s.confirmed_drivers.length} sub="Triad-validated only" tone="ai" testId="cc-live-drivers" />
        <KpiTile label="SOPs" value={`${s.sops.active} / ${s.sops.in_draft}`} sub="active / in draft" tone="neutral" testId="cc-live-sops" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--green] mb-1.5">Top confirmed drivers</div>
          {s.confirmed_drivers.length === 0 && <p className="text-[11px] text-[--tx-3]">None validated yet - findings await the Triad gate in Agentic Analysis Runs.</p>}
          {s.confirmed_drivers.map((d, i) => (
            <div key={i} className="text-[11px] text-[--tx-2] mb-1.5 leading-snug">
              <span className="font-mono text-[9px] text-[--tx-3] mr-1">{d.agent}</span>{d.title}
            </div>
          ))}
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--ai] mt-3 mb-1.5">Top hypotheses</div>
          {s.top_hypotheses.map((h) => (
            <div key={h.id} className="text-[11px] text-[--tx-2] mb-1"><span className="font-mono text-[9px] text-[--tx-3] mr-1">{h.id}</span>{h.title}</div>
          ))}
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--amber] mb-1.5">Open decisions</div>
          {s.open_decisions.map((d) => (
            <div key={d.id} className="text-[11px] text-[--tx-2] mb-1.5 leading-snug">
              <span className="font-mono text-[9px] text-[--tx-3] mr-1">{d.id}</span>{d.title}
              <span className="block text-[10px] text-[--tx-3]">{d.owner}</span>
            </div>
          ))}
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--amber] mt-3 mb-1.5">Top missing datasets</div>
          {s.missing_datasets.slice(0, 3).map((m, i) => (
            <div key={i} className="text-[11px] text-[--tx-2] mb-1 leading-snug">{m.category} <span className="text-[10px] text-[--tx-3]">({m.priority})</span></div>
          ))}
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1.5">Pilot readiness</div>
          <p className="text-[11px] text-[--tx-2] mb-3">{s.pilot_readiness}</p>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1.5">Operating cadence</div>
          <p className="text-[11px] text-[--tx-2] mb-3">{s.operating_cadence}</p>
          <div className="rounded-lg border border-[--divider] bg-[--panel-dark] px-3 py-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[--green]">Benefit & EBITDA tracking</div>
            <p className="text-[11px] text-[--tx-2] mt-1 leading-snug">{s.benefit_tracking.note}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-3 pt-2 border-t border-[--divider]">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/live/export/executive-packet.pdf`}
          data-testid="cc-live-packet-button"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-3 py-1.5 text-[11px] font-semibold text-white transition-colors mr-auto">
          <FileDown size={11} /> Executive packet (PDF)
        </a>
        {[["/live/forensic", "August Forensic"], ["/live/runs", "Agent Runs"], ["/live/sop", "SOP Builder"]].map(([to, label]) => (
          <button key={to} onClick={() => navigate(to)} data-testid={`cc-live-link-${to.split("/").pop()}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[--ai] hover:text-[#35B7B1] transition-colors">
            {label} <ArrowUpRight size={11} />
          </button>
        ))}
      </div>
    </Panel>
  );
};

// Live adoption plans block for the Change Management module (linked from approved SOPs).
export const LiveAdoptionPlansPanel = () => {
  const { data: sops } = useFetch("/live/sops?status=active");
  if (!sops) return null;
  return (
    <Panel title="Live engagement - adoption plans from approved SOPs" testId="change-live-adoption-panel"
      right={<LiveBadge />} className="mb-5">
      {sops.length === 0 ? (
        <EmptyState message="No approved SOPs yet. When an SOP is activated in the SOP Builder, its adoption plan appears here." />
      ) : (
        <div className="space-y-2">
          {sops.map((s) => (
            <div key={s.id} className="rounded-lg border border-[--hairline] bg-[--shell-2] px-3 py-2.5 flex items-center justify-between gap-3" data-testid={`change-live-sop-${s.id}`}>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-[--tx-1] truncate">{s.process_name}</div>
                <div className="text-[10px] font-mono text-[--tx-3]">v{s.version} · approved by {s.approved_by} · {(s.approved_at || "").slice(0, 10)}</div>
              </div>
              <StatusChip tone="validated">Active - adoption in plan</StatusChip>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};
